import React, { useEffect, useState } from 'react';
import styles from './HorizontalSwitcher.module.css';
import clsx from 'clsx';
import BzmIcon from '../icons/BzmIcon';
import { useIsMount } from '../../hooks/useIsMountHook';
import _ from 'lodash';

interface SwitcherPanel {
  title: string;
  content: any;
}

interface SwitcherProps {
  leftPanel: SwitcherPanel;
  rightPanel: SwitcherPanel;
  /** Size of the panel with hints (clickable grey box) in percentages. */
  swapPanelWidth?: number;
  /** Height of the Switcher. */
  height?: number;
  /** Allows to control the swapping logic from outside */
  isSwapped?: boolean;
  toggle?: () => void;
}

/**
 * Horizontal switcher component.
 * @constructor
 */
const HorizontalSwitcher = (props: SwitcherProps) => {
  const [isSwapped, setIsSwapped] = useState(!_.isNil(props.isSwapped) ? props.isSwapped : false);
  const shrunkSize = props.swapPanelWidth ? props.swapPanelWidth : 15;
  const fullSize = 100 - shrunkSize + '%';
  const height = props.height ? props.height + 'px' : '100px';
  const { rightPanel, leftPanel } = props;
  const isMount = useIsMount();

  const toggle = () => {
    //If we are just using internal state, toggle it here
    //otherwise component is driven by the parent
    if (_.isNil(props.toggle)) {
      setIsSwapped(!isSwapped);
    } else {
      props.toggle();
    }
  };

  useEffect(() => {
    if (!isMount && !_.isNil(props.isSwapped)) {
      setIsSwapped(props.isSwapped);
    }
  }, [props.isSwapped]);

  return (
    <div className={'clear-both position-relative'} style={{ minHeight: height }}>
      {/*Switcher Component*/}
      <div
        style={{ width: shrunkSize + '%', minHeight: height, left: isSwapped ? 0 : fullSize }}
        className={clsx(styles.greyPanel, 'cursor-pointer')}
        onClick={toggle}
      >
        <div className={'text-center'}>{isSwapped ? rightPanel.title : leftPanel.title}</div>
        <button
          className={clsx(styles.arrow)}
          style={{ left: !isSwapped ? '-15px' : 'calc(100% - 15px)' }}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            toggle();
          }}
        >
          <BzmIcon
            size={20}
            variant={!isSwapped ? 'icon-bzm-arrow-left' : 'icon-bzm-arrow-right'}
            style={{ bottom: 3, position: 'relative' }}
          />
        </button>
      </div>

      {/* Left Panel */}
      {!isSwapped && (
        <div className={'position-relative'} style={{ width: fullSize, left: 0 }}>
          {leftPanel.content}
        </div>
      )}

      {/* Right Panel */}
      {isSwapped && (
        <div className={'position-absolute'} style={{ width: fullSize, right: 0 }}>
          {rightPanel.content}
        </div>
      )}
    </div>
  );
};

export default HorizontalSwitcher;
